import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, Info, TriangleAlert, X, Icon } from "lucide-react"
import { cn } from '@/lib/utils'

type ToastKind = 'success' | 'error' | 'info'
interface ToastItem {
  id: number
  kind: ToastKind
  message: string
}

interface ToastValue {
  toast: (message: string, kind?: ToastKind) => void
}

const ToastContext = createContext<ToastValue>({ toast: () => undefined })

const kindStyles: Record<ToastKind, { icon: typeof Info; className: string }> = {
  success: { icon: CheckCircle2, className: 'bg-success-soft text-success' },
  error: { icon: TriangleAlert, className: 'bg-error-soft text-error' },
  info: { icon: Info, className: 'bg-surface-2 text-text-muted' },
}

/** Thông báo nổi — dùng sau khi lưu/xoá để người dùng biết thao tác đã chạy. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])

  const remove = useCallback((id: number) => setItems((list) => list.filter((t) => t.id !== id)), [])

  const toast = useCallback(
    (message: string, kind: ToastKind = 'success') => {
      const id = Date.now() + Math.random()
      setItems((list) => [...list, { id, kind, message }])
      window.setTimeout(() => remove(id), 4000)
    },
    [remove],
  )

  const value = useMemo(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed z-[60] bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:right-5 sm:translate-x-0 flex flex-col gap-2 w-[min(92vw,380px)]">
            {items.map((t) => {
              const { icon: Icon, className } = kindStyles[t.kind]
              return (
                <div
                  key={t.id}
                  className="flex items-start gap-3 p-3.5 rounded-squircle bg-surface border border-border-strong shadow-float animate-fade-up"
                >
                  <span className={cn('h-8 w-8 shrink-0 grid place-items-center rounded-squircle', className)}>
                    <Icon size={16} />
                  </span>
                  <p className="flex-1 text-sm text-text leading-snug pt-1.5">{t.message}</p>
                  <button
                    type="button"
                    onClick={() => remove(t.id)}
                    aria-label="close"
                    className="shrink-0 text-text-faint hover:text-text transition-colors pt-1.5"
                  >
                    <X size={15} />
                  </button>
                </div>
              )
            })}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  )
}

export function useToast() {
  return useContext(ToastContext)
}
